export type CreditItemConfig = {
  match: RegExp;
  title: (name: string) => string;
  tableTitle: string;
  countLabel?: string;
  // Key of the itemData total that is the same number as the count row, so it is not shown twice.
  countKey?: string;
  // Same guideline checks the backend uses to raise the resolution (BorrowerCreditReviewParseUtil).
  minimum?: number;
  // Shown as a row when the item has no count (account categories).
  categoryRow?: boolean;
  // Only items whose wording was approved have these; the rest show the table alone.
  ask?: string | ((name: string) => string);
};

const PAST_DUE_CATEGORY = /^(Mortgage|HELOC\/Credit Line|Education|Auto|Revolving|Derogatory|Other)$/i;

export const CREDIT_ITEMS: CreditItemConfig[] = [
  {
    match: /^Number of Seasoned Tradelines$/i,
    title: () => 'No credit account older than 2 years',
    tableTitle: 'Tradeline summary',
    countLabel: 'Seasoned tradelines (open 2+ years)',
    countKey: 'seasoningTradelines',
    minimum: 1,
    ask: 'We need at least one tradeline (credit card, loan or other credit account) that has been open for 2 years or more. None of the accounts on your credit report meet that yet.',
  },
  {
    match: /^Number of Current Active Tradelines$/i,
    title: () => 'Not enough current credit accounts',
    tableTitle: 'Tradeline summary',
    countLabel: 'Current active tradelines',
    countKey: 'activeTradeline',
    minimum: 2,
    ask: 'The credit report shows fewer than 2 current active tradelines (accounts with a balance that are not past due).',
  },
  {
    match: /^Number of Tradelines$/i,
    title: () => 'Not enough credit accounts',
    tableTitle: 'Tradeline summary',
    countLabel: 'Tradelines on the credit report',
    countKey: 'tradelinesCount',
    minimum: 3,
    ask: 'The credit report shows fewer than 3 tradelines (credit cards, loans or other credit accounts).',
  },
  {
    match: /^Number of 30 Day Lates within 12 Months$/i,
    title: () => 'Late payments in the last 12 months',
    tableTitle: '30+ day late payments, last 12 months',
    countLabel: 'Late payments on mortgage accounts',
    ask: 'Your credit report shows payments made 30 or more days late in the last 12 months. Tell us what happened and whether the accounts are current now.',
  },
  {
    match: PAST_DUE_CATEGORY,
    title: name => `${name} tradelines past due`,
    tableTitle: 'Accounts on the credit report',
    categoryRow: true,
    ask: name => `${name} tradelines on the credit report are past due.`,
  },
  {
    match: /^SBA$/i,
    title: () => 'SBA tradelines',
    tableTitle: 'Accounts on the credit report',
    categoryRow: true,
  },
];

export const findCreditItem = (name: string) => CREDIT_ITEMS.find(item => item.match.test(name));
